import React from "react";

import { Stack, Text, useTheme } from "@bps/fluent-ui";
import { useCurrentUserQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";
import { verifyHasPermissions } from "@libs/permissions/permissions.utils";

import { SelectionListItems } from "./SelectionListItem";

export interface Item {
  id: string;
  name: string;
  node?: React.ReactNode;
  disabled?: boolean;
  showToolTip?: boolean;
  requiredPermissions?: Permissions | Permissions[];
  group?: string;
}

export interface GroupItem {
  groupName?: string;
  items?: Item[];
}

interface SelectionListProps {
  title?: string;
  items: Item[];
  selectedItem?: Item;
  onSelectedItemChange: (item: Item) => void;
}

export const SelectionList = ({
  title,
  items,
  selectedItem,
  onSelectedItemChange
}: SelectionListProps) => {
  const theme = useTheme();
  const { data: user } = useCurrentUserQuery();
  const userPermissions = user?.impersonatingRole
    ? user.impersonatingRole.permissions
    : user?.permissions || [];

  const filteredItems = items.filter(item =>
    verifyHasPermissions(userPermissions, item.requiredPermissions)
  );

  if (!filteredItems.length) {
    return null;
  }

  const uniqueGroups = filteredItems
    .filter(
      // filter for unique groups
      (value, index, array) =>
        array.findIndex(v => v.group === value.group) === index
    )
    .map(
      val =>
        ({
          groupName: val.group ?? "Miscellaneous",
          items: filteredItems.filter(f => f.group === val.group)
        } as GroupItem)
    );

  return (
    <Stack tokens={{ childrenGap: theme.spacing.s1 }}>
      {title && <Text variant="large">{title}</Text>}
      <Stack tokens={{ childrenGap: theme.spacing.s2 }}>
        {uniqueGroups.length > 1 ? (
          uniqueGroups.map(group => (
            <Stack
              tokens={{ childrenGap: theme.spacing.s2 }}
              key={group.groupName}
            >
              <Text variant="medium">{group.groupName}</Text>
              <SelectionListItems
                onSelectedItemChange={onSelectedItemChange}
                items={group.items}
                selectedItem={selectedItem}
              />
            </Stack>
          ))
        ) : (
          <SelectionListItems
            onSelectedItemChange={onSelectedItemChange}
            items={filteredItems}
            selectedItem={selectedItem}
          />
        )}
      </Stack>
    </Stack>
  );
};
